<template>
  <!-- Absender, Empfänger & Richtung festlegen (Kundenfeedback 02.10.2026,
       erster Teil, III; Fehlermeldung Polz 16.10.2026): Wer sich eine E-Mail
       selbst an die zentrale Adresse schickt, sie aus Outlook weiterleitet
       oder mit KARS im Bcc versendet, sagt KARS hier, ob sie eingegangen oder
       gesendet ist, von wem sie stammt bzw. an wen sie ging – bei gesendeten
       Mails als Liste aller Empfänger (An/Cc), nachträglich änderbar. -->
  <v-dialog max-width="600" :model-value="Boolean(message)" @update:model-value="!$event && $emit('close')">
    <section :aria-label="$gt('Absender, Empfänger & Richtung festlegen')" class="dd" data-testid="chat-direction-dialog">
      <header class="dd__head">
        <span aria-hidden="true" class="dd__icon"><ArrowLeftRight :size="18" /></span>
        <div class="dd__titles">
          <h2 class="dd__title">{{ $gt('Absender, Empfänger & Richtung festlegen') }}</h2>
          <p class="dd__sub">{{ subject || $gt('(Kein Betreff)') }}</p>
        </div>
      </header>

      <p v-if="loading" class="dd__state">{{ $gt('Wird geladen…') }}</p>
      <p v-else-if="loadError" class="dd__state dd__state--error" role="alert">{{ $gt(loadError) }}</p>

      <template v-else-if="info">
        <p v-if="info.forwardedDetected" class="dd__detected" data-testid="chat-direction-detected">
          <Sparkles :size="14" />
          <span>{{ $gt('Diese E-Mail sieht nach einer Weiterleitung aus. Den ursprünglichen Absender haben wir aus dem Text übernommen – bitte prüfen.') }}</span>
        </p>

        <fieldset class="dd__group">
          <legend class="dd__legend">{{ $gt('Was ist das für eine E-Mail?') }}</legend>
          <div class="dd__choices" role="radiogroup">
            <button
              v-for="choice in choices"
              :key="choice.value"
              :aria-checked="form.direction === choice.value ? 'true' : 'false'"
              class="dd__choice"
              :class="{ 'dd__choice--on': form.direction === choice.value }"
              :data-testid="`chat-direction-${choice.value}`"
              role="radio"
              type="button"
              @click="form = { ...form, direction: choice.value }"
            >
              <component :is="choice.icon" class="dd__choice-icon" :size="18" />
              <span class="dd__choice-title">{{ choice.title }}</span>
              <span class="dd__choice-text">{{ choice.text }}</span>
            </button>
          </div>
        </fieldset>

        <fieldset class="dd__group">
          <legend class="dd__legend">
            {{ form.direction === 'in' ? $gt('Von wem stammt die E-Mail?') : $gt('An wen ging die E-Mail?') }}
          </legend>
          <div v-if="chips.length > 0" class="dd__chips">
            <button
              v-for="chip in chips"
              :key="chip.email"
              class="dd__chip"
              :class="{ 'dd__chip--on': chipActive(chip) }"
              type="button"
              @click="pickChip(chip)"
            >
              <span class="dd__chip-kind">{{ chip.source === 'forwarded' ? $gt('Aus der Weiterleitung') : $gt('Bisher') }}</span>
              <span class="dd__chip-mail">{{ chip.name ? `${chip.name} · ${chip.email}` : chip.email }}</span>
            </button>
          </div>

          <!-- Eingegangen: genau ein Absender. -->
          <div v-if="form.direction === 'in'" class="dd__fields">
            <label class="dd__field">
              <span>{{ $gt('E-Mail-Adresse des Absenders') }}</span>
              <input
                ref="emailInput"
                autocomplete="off"
                class="dd__input"
                :class="{ 'dd__input--error': touched && error }"
                data-testid="chat-direction-email"
                inputmode="email"
                :placeholder="$gt('name@beispiel.de')"
                type="email"
                :value="form.email"
                @blur="touched = true"
                @input="form = { ...form, email: ($event.target as HTMLInputElement).value }"
                @keydown.enter.prevent="apply"
              >
            </label>
            <label class="dd__field">
              <span>{{ $gt('Name (optional)') }}</span>
              <input
                autocomplete="off"
                class="dd__input"
                data-testid="chat-direction-name"
                :maxlength="NAME_MAX_LENGTH"
                :placeholder="$gt('z. B. Autohaus Müller')"
                type="text"
                :value="form.name"
                @input="form = { ...form, name: ($event.target as HTMLInputElement).value }"
                @keydown.enter.prevent="apply"
              >
            </label>
          </div>

          <!-- Gesendet: alle Empfänger (An/Cc) – nachträglich änderbar
               (Fehlermeldung Polz 16.10.2026: Outlook-Mail, KARS nur im Bcc). -->
          <template v-else>
            <ul class="dd__recipients" data-testid="chat-direction-recipients">
              <li v-for="(r, i) in form.recipients" :key="i" class="dd__recipient">
                <select
                  :aria-label="$gt('An oder Cc')"
                  class="dd__input dd__role"
                  :data-testid="`chat-direction-recipient-role-${i}`"
                  :value="r.role"
                  @change="setRecipient(i, { role: ($event.target as HTMLSelectElement).value === 'cc' ? 'cc' : 'to' })"
                >
                  <option value="to">{{ $gt('An') }}</option>
                  <option value="cc">Cc</option>
                </select>
                <input
                  :ref="el => setRecipientInputRef(i, el)"
                  :aria-label="$gt('E-Mail-Adresse des Empfängers')"
                  autocomplete="off"
                  class="dd__input"
                  :class="{ 'dd__input--error': touched && !!recipientError(r) }"
                  :data-testid="`chat-direction-recipient-email-${i}`"
                  inputmode="email"
                  :placeholder="$gt('name@beispiel.de')"
                  type="email"
                  :value="r.email"
                  @blur="touched = true"
                  @input="setRecipient(i, { email: ($event.target as HTMLInputElement).value })"
                  @keydown.enter.prevent="apply"
                >
                <input
                  :aria-label="$gt('Name (optional)')"
                  autocomplete="off"
                  class="dd__input"
                  :data-testid="`chat-direction-recipient-name-${i}`"
                  :maxlength="NAME_MAX_LENGTH"
                  :placeholder="$gt('Name (optional)')"
                  type="text"
                  :value="r.name"
                  @input="setRecipient(i, { name: ($event.target as HTMLInputElement).value })"
                  @keydown.enter.prevent="apply"
                >
                <button
                  :aria-label="$gt('Empfänger entfernen')"
                  class="dd__remove"
                  :data-testid="`chat-direction-recipient-remove-${i}`"
                  :title="$gt('Empfänger entfernen')"
                  type="button"
                  @click="form = removeRecipient(form, i)"
                >
                  <X :size="14" />
                </button>
              </li>
            </ul>
            <button class="dd__add" data-testid="chat-direction-add-recipient" type="button" @click="addEmptyRecipient">
              <Plus :size="14" /><span>{{ $gt('Empfänger hinzufügen') }}</span>
            </button>
          </template>
          <p v-if="touched && error" class="dd__error" role="alert">{{ $gt(error) }}</p>
          <p class="dd__hint">{{ consequence }}</p>
        </fieldset>
      </template>

      <footer class="dd__foot">
        <button class="dd__btn" type="button" @click="$emit('close')">{{ $gt('Abbrechen') }}</button>
        <button
          class="dd__btn dd__btn--primary"
          data-testid="chat-direction-apply"
          :disabled="!info || busy"
          type="button"
          @click="apply"
        >
          {{ busy ? $gt('Wird gespeichert…') : $gt('Übernehmen') }}
        </button>
      </footer>
    </section>
  </v-dialog>
</template>

<script setup lang="ts">
  import type { ChatMessage } from './chatModel'
  import { ArrowLeftRight, Inbox, Plus, Send, Sparkles, X } from 'lucide-vue-next'
  import { computed, nextTick, ref, watch } from 'vue'
  import { gt } from '@/i18n/gt'
  import { fetchDirectionInfo } from './chatApi'
  import { normalizeEmail } from './chatModel'
  import {
    addRecipient,
    type ClassifyForm,
    type ClassifyPayload,
    classifyPayload,
    type ClassifyRecipient,
    type ClassifySuggestion,
    type DirectionInfo,
    initialClassifyForm,
    isValidEmail,
    removeRecipient,
    suggestionChips,
    updateRecipient,
    validateClassify,
  } from './directionClassify'

  /** Wie das Backend (maxCounterpartNameLen). */
  const NAME_MAX_LENGTH = 200

  const props = defineProps<{ message: ChatMessage | null, subject?: string | null, busy: boolean }>()
  const emit = defineEmits<{ close: [], apply: [payload: ClassifyPayload] }>()

  const info = ref<DirectionInfo | null>(null)
  const loading = ref(false)
  const loadError = ref('')
  const touched = ref(false)
  const form = ref<ClassifyForm>({ direction: 'in', email: '', name: '', recipients: [] })
  const emailInput = ref<HTMLInputElement | null>(null)
  const recipientInputs = new Map<number, HTMLInputElement>()
  function setRecipientInputRef (index: number, el: unknown) {
    if (el instanceof HTMLInputElement) recipientInputs.set(index, el)
    else recipientInputs.delete(index)
  }

  function setRecipient (index: number, patch: Partial<ClassifyRecipient>) {
    form.value = updateRecipient(form.value, index, patch)
  }

  /** Leere Zeile anhängen und den Cursor hineinsetzen; leere Zeilen werden beim Speichern übergangen. */
  async function addEmptyRecipient () {
    form.value = { ...form.value, recipients: [...form.value.recipients, { email: '', name: '', role: 'to' }] }
    await nextTick()
    recipientInputs.get(form.value.recipients.length - 1)?.focus()
  }

  function recipientError (r: ClassifyRecipient): string {
    const email = r.email.trim()
    if (!email) return ''
    if (!isValidEmail(email)) return 'Bitte eine gültige E-Mail-Adresse angeben'
    return form.value.recipients.filter(x => normalizeEmail(x.email) === normalizeEmail(email)).length > 1
      ? 'Eine E-Mail-Adresse ist doppelt eingetragen'
      : ''
  }

  function chipActive (chip: ClassifySuggestion): boolean {
    return form.value.direction === 'in'
      ? chip.email === normalizedEmail.value
      : form.value.recipients.some(r => normalizeEmail(r.email) === chip.email)
  }

  /** Vorschlag übernehmen: bei „eingegangen" als Absender, bei „gesendet" als weiterer Empfänger. */
  function pickChip (chip: ClassifySuggestion) {
    form.value = form.value.direction === 'in'
      ? { ...form.value, email: chip.email, name: chip.name }
      : addRecipient(form.value, { email: chip.email, name: chip.name, role: 'to' })
  }

  const choices = computed(() => [
    { value: 'in' as const, icon: Inbox, title: gt('Eingegangen'), text: gt('Wir haben diese E-Mail erhalten.') },
    { value: 'out' as const, icon: Send, title: gt('Gesendet'), text: gt('Wir haben diese E-Mail verschickt.') },
  ])
  const chips = computed(() => (info.value ? suggestionChips(info.value) : []))
  const normalizedEmail = computed(() => normalizeEmail(form.value.email))
  const error = computed(() => validateClassify(form.value))

  /** Was nach „Übernehmen" passiert – in einem Satz. */
  const consequence = computed(() => {
    if (form.value.direction === 'out') {
      if (!form.value.recipients.some(r => r.email.trim())) {
        return gt('Ohne Empfänger bleibt die bisherige Gegenseite bestehen; nur die Richtung wird geändert.')
      }
      return info.value?.onlyMessage
        ? gt('Die Liste ersetzt die bisherigen Empfänger dieser E-Mail; der Verlauf wird unter dem ersten Empfänger geführt.')
        : gt('Diese Unterhaltung enthält mehrere Nachrichten: Die Empfänger werden als weitere Beteiligte ergänzt, bestehende Beteiligte bleiben erhalten.')
    }
    if (!normalizedEmail.value) {
      return gt('Ohne Angabe bleibt die bisherige Gegenseite bestehen; nur die Richtung wird geändert.')
    }
    if (info.value?.inCase) {
      return gt('In der Akte bleiben alle Beteiligten der Unterhaltung erhalten: Die Angabe wird ergänzt. Steht bisher nur Ihre eigene Adresse als Empfänger, wird sie durch diese Angabe ersetzt.')
    }
    if (info.value?.singleMessage) {
      return form.value.direction === 'in'
        ? gt('Die E-Mail steht danach im Posteingang und der Verlauf wird unter diesem Absender geführt.')
        : gt('Die E-Mail steht danach unter „Gesendet“ und der Verlauf wird unter diesem Empfänger geführt.')
    }
    return gt('Diese Unterhaltung enthält mehrere Nachrichten: Die Angabe wird als weiterer Beteiligter ergänzt, bestehende Beteiligte bleiben erhalten.')
  })

  watch(() => props.message?.id, async id => {
    info.value = null
    loadError.value = ''
    touched.value = false
    if (!id) return
    loading.value = true
    try {
      const loaded = await fetchDirectionInfo(id)
      // Der Dialog kann inzwischen für eine andere Nachricht offen sein.
      if (props.message?.id !== id) return
      info.value = loaded
      form.value = initialClassifyForm(loaded)
      await nextTick()
      emailInput.value?.focus()
    } catch (error_) {
      if (props.message?.id === id) loadError.value = (error_ as Error).message
    } finally {
      if (props.message?.id === id) loading.value = false
    }
  }, { immediate: true })

  function apply () {
    touched.value = true
    if (!info.value || props.busy || error.value) return
    emit('apply', classifyPayload(form.value))
  }
</script>

<style scoped>
.dd { display: flex; flex-direction: column; gap: 16px; padding: 22px; border-radius: 16px; background: #fff; }
.dd__head { display: flex; align-items: center; gap: 12px; }
.dd__icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: var(--primary-light-color);
  color: var(--primary-color);
}
.dd__titles { min-width: 0; }
.dd__title { margin: 0; font-size: 1.02rem; font-weight: 700; color: var(--dark-color); letter-spacing: -0.01em; }
.dd__sub {
  margin: 2px 0 0;
  overflow: hidden;
  font-size: 0.8rem;
  color: var(--grey-color);
  white-space: nowrap;
  text-overflow: ellipsis;
}
.dd__state { margin: 8px 0; text-align: center; font-size: 0.84rem; color: var(--grey-color); }
.dd__state--error { color: #c62828; }
.dd__detected {
  display: flex;
  gap: 8px;
  margin: 0;
  padding: 9px 12px;
  border-radius: 10px;
  background: var(--primary-light-color);
  font-size: 0.78rem;
  line-height: 1.45;
  color: var(--dark-color);
}
.dd__detected svg { flex-shrink: 0; margin-top: 2px; color: var(--primary-color); }
.dd__group { display: flex; flex-direction: column; gap: 8px; margin: 0; padding: 0; border: none; min-width: 0; }
.dd__legend { padding: 0; font-size: 0.74rem; font-weight: 700; color: var(--grey-color); letter-spacing: 0.03em; text-transform: uppercase; }
.dd__choices { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.dd__choice {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 10px;
  align-items: center;
  padding: 12px 14px;
  border: 1.5px solid var(--grey-medium-color);
  border-radius: 12px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}
.dd__choice:hover { border-color: var(--primary-color); }
.dd__choice:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.dd__choice--on {
  border-color: var(--primary-color);
  background: var(--primary-light-color);
  box-shadow: 0 4px 14px rgba(0, 118, 229, 0.14);
}
.dd__choice-icon { grid-row: span 2; color: var(--grey-color); }
.dd__choice--on .dd__choice-icon { color: var(--primary-color); }
.dd__choice-title { font-size: 0.9rem; font-weight: 700; color: var(--dark-color); }
.dd__choice-text { font-size: 0.76rem; color: var(--grey-color); }
.dd__chips { display: flex; flex-wrap: wrap; gap: 6px; }
.dd__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 4px 10px 4px 4px;
  border: 1px solid var(--grey-medium-color);
  border-radius: 999px;
  background: #fff;
  font-size: 0.76rem;
  cursor: pointer;
}
.dd__chip:hover, .dd__chip--on { border-color: var(--primary-color); background: var(--primary-light-color); }
.dd__chip:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 1px; }
.dd__chip-kind {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--grey-light-color);
  font-size: 0.66rem;
  font-weight: 700;
  color: var(--grey-color);
}
.dd__chip-mail { overflow: hidden; color: var(--dark-color); white-space: nowrap; text-overflow: ellipsis; }
.dd__fields { display: grid; grid-template-columns: 1.2fr 1fr; gap: 10px; }
.dd__field { display: flex; flex-direction: column; gap: 4px; min-width: 0; font-size: 0.74rem; font-weight: 600; color: var(--grey-color); }
.dd__input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--border-grey-color);
  border-radius: 10px;
  font-size: 0.86rem;
  color: var(--dark-color);
}
.dd__input:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(0, 118, 229, 0.14); }
.dd__input--error { border-color: #c62828; }
.dd__error { margin: 0; font-size: 0.76rem; font-weight: 600; color: #c62828; }
/* Empfängerliste bei „gesendet": Rolle · Adresse · Name · Entfernen */
.dd__recipients { display: flex; flex-direction: column; gap: 8px; margin: 0; padding: 0; list-style: none; }
.dd__recipient { display: grid; grid-template-columns: 72px 1.3fr 1fr auto; gap: 8px; align-items: center; min-width: 0; }
.dd__role { padding-right: 4px; }
.dd__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid var(--grey-medium-color);
  border-radius: 9px;
  background: #fff;
  color: var(--grey-color);
  cursor: pointer;
}
.dd__remove:hover { border-color: #c62828; color: #c62828; }
.dd__remove:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 1px; }
.dd__add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 6px 10px;
  border: 1px dashed var(--primary-color);
  border-radius: 9px;
  background: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--primary-color);
  cursor: pointer;
}
.dd__add:hover { background: var(--primary-light-color); }
.dd__add:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.dd__hint { margin: 0; font-size: 0.76rem; line-height: 1.45; color: var(--grey-color); }
.dd__foot { display: flex; justify-content: flex-end; gap: 8px; }
.dd__btn {
  padding: 9px 16px;
  border: 1px solid var(--border-grey-color);
  border-radius: 10px;
  background: #fff;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
}
.dd__btn:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.dd__btn--primary { border-color: var(--primary-color); background: var(--primary-color); color: #fff; }
.dd__btn:disabled { opacity: 0.5; cursor: default; }
@media (max-width: 520px) {
  .dd__choices, .dd__fields { grid-template-columns: 1fr; }
  .dd__recipient { grid-template-columns: 72px 1fr auto; }
  .dd__recipient input[type="text"] { grid-column: 1 / -1; }
}
@media (prefers-reduced-motion: reduce) {
  .dd__choice { transition: none; }
}
</style>
